// The shape both the llm-trace endpoint and a stored replay run get folded
// into, so the timeline renderer and the diff work on one structure.
//
// Why a replay needs its own adapter at all: the replay session is hard deleted
// as soon as the run finishes, so `llm-trace` 404s on it. The durable
// `qa_replay_run` snapshot is all that survives — it carries timestamps rather
// than offsets, and no sub-agent rows.

import { arrayValue, intValue, mapValue, stringValue } from "@/utils/coerce";

export type TraceNodeKind =
  | "turn"
  | "tool_call"
  /** A batch of simultaneous tool calls — the envelope they run inside. */
  | "tool_batch"
  /** A whole delegate_tasks call — the envelope its durable jobs run inside. */
  | "async_delegation"
  /** One durable job. Wider than the execution it holds; the rest is queue wait. */
  | "async_job"
  | "cw_span"
  /** The sub-agent execution as a whole — the envelope its rounds run inside. */
  | "sa_execution"
  | "sa_model_turn"
  | "sa_tool_call";

export type TraceLaneKind =
  | "main"
  | "tool_batch"
  | "async_delegation"
  | "citation_worker"
  | "sub_agent"
  | "live_session";

/** Normalized status, collapsed from the per-table vocabularies. */
export type TraceStatus = "ok" | "error" | "running" | "cancelled" | "unknown";

/**
 * One node's token cost, provider-neutral.
 *
 * `input` is the WHOLE prompt including whatever was served from or written to
 * the cache, which is how both providers report it. Subtract `cachedInput` and
 * `cacheWriteInput` for the part that was paid for at the plain input rate.
 */
export type NodeTokens = {
  input: number | null;
  cachedInput: number | null;
  /**
   * Part of `input` written to the provider's cache on this call (OpenRouter
   * only). Billed ABOVE the input rate — 1.25x on Claude and GPT-6 — so a
   * prefix that changes every call costs more than no caching at all.
   */
  cacheWriteInput: number | null;
  /** What the provider billed for this call, in USD (OpenRouter `usage.cost`). */
  costUsd: number | null;
  output: number | null;
  thoughts: number | null;
  total: number | null;
};

export type TraceNode = {
  id: string;
  kind: TraceNodeKind;
  label: string;
  startMs: number | null;
  endMs: number | null;
  /**
   * Main-flow turns only. `startMs → inferenceEndMs` is the model thinking;
   * after it the turn is waiting on tools and sub-agents.
   */
  inferenceEndMs?: number | null;
  /**
   * The parts of a turn's wait window where none of its own tools were running.
   *
   * The window as a whole is not "waiting" — most of it is a tool executing,
   * drawn as its own bar over this one. What is left is the real wait:
   * orchestration between calls, or a sub-agent running in another lane.
   */
  waitSegments?: Array<{ startMs: number; endMs: number }>;
  status: TraceStatus;
  /** Verbatim status from the payload, for display. */
  rawStatus: string;
  durationMs: number | null;
  model?: string;
  provider?: string;
  /** True when the turn only called the citation-finalization barrier. */
  finalizeAnswer?: boolean;
  /** True when a delegate_tasks result was slimmed into its own sub_agent lane. */
  resultSlimmed?: boolean;
  /**
   * Started but never recorded an end — a tool call still `pending`, or a turn
   * with no `latency_ms` and no tools. The node has no measurable duration and
   * must not be dropped: a hung tool call is the thing QA is looking for.
   */
  openEnded?: boolean;
  /**
   * Position on the axis is reconstructed, not measured.
   *
   * A stored replay run only kept per-call durations, so its sub-agent tool
   * calls are laid out end to end from the dispatch start. The widths are real;
   * where they sit is not, and the UI has to say so.
   */
  approximate?: boolean;
  /**
   * Raw payload for the detail panel. Held by reference and never serialized
   * until a node is opened — a long trace with several sub-agents runs to
   * megabytes and the endpoint has no size cap.
   */
  detail: Record<string, unknown>;
  /** Present on nodes that made a model call. Absent on tool calls and envelopes. */
  tokens?: NodeTokens;
  children: TraceNode[];
};

export type TraceLane = {
  id: string;
  kind: TraceLaneKind;
  label: string;
  /** Secondary identifier — a sub-agent's `task_id`, which is human-meaningful. */
  sublabel?: string;
  startMs: number | null;
  endMs: number | null;
  /** Node that spawned this lane, for hover linkage back to the main flow. */
  parentNodeId: string | null;
  /**
   * Lane this one is drawn inside — a job under the delegation that submitted
   * it. A pointer rather than real nesting: `lanes` stays flat so anything that
   * walks or pairs lanes (the replay diff, the node list, the metrics) keeps
   * seeing every lane, and only the renderer builds the tree.
   */
  parentLaneId?: string;
  status?: TraceStatus;
  rawStatus?: string;
  /**
   * Flow-level payload, when the flow carries one of its own. A sub-agent
   * execution holds what it was asked to do and what it concluded, none of
   * which lives on its child nodes.
   */
  detail?: Record<string, unknown>;
  /**
   * Siblings from one dispatch share this (`batch_id`). They are NOT adjacent
   * in `lanes` — the server interleaves all flows by start offset — so the
   * relationship has to be shown rather than inferred from position.
   */
  groupId?: string;
  nodes: TraceNode[];
};

export type TraceMetrics = {
  ttftMs: number | null;
  totalMs: number | null;
  turnCount: number;
  toolCount: number;
  errorCount: number;
  subAgentCount: number;
  citationWorkerCount: number;
  tokensIn: number | null;
  /** Part of `tokensIn` that was served from cache, not billed at full rate. */
  tokensCached: number | null;
  /** Part of `tokensIn` written to the cache, billed above the input rate. */
  tokensCacheWrite: number | null;
  /** Provider-billed USD, summed over the calls that report it. */
  costUsd: number | null;
  tokensOut: number | null;
  /** Reasoning tokens. Often the largest line item, and billed. */
  tokensThoughts: number | null;
  tokensTotal: number | null;
};

/**
 * One body this message carried, and when.
 *
 * A delegated answer is published as an acknowledgement and then overwritten in
 * place — same id, and `edited_at` is never set — by an interim report as each
 * task finishes, and finally by the whole report. So the content on screen is
 * the last of these while the trace under it belongs to the acknowledgement
 * turn, which is the only turn there is.
 */
export type TraceRevision = {
  kind: string;
  offsetMs: number | null;
};

export type NormalizedTrace = {
  source: "llm_trace" | "replay_snapshot";
  messageId: string | null;
  /** Empty unless the body was replaced after publication. */
  revisions: TraceRevision[];
  timeline: {
    firstStartMs: number | null;
    lastEndMs: number | null;
    /**
     * Trigger to answer — deliberately NOT the spec's `total_span_ms`, which is
     * the min/max across nodes and so stops before the answer finishes
     * streaming. Renamed to keep the two from being read as the same quantity:
     * the replay side can only measure trigger-to-answer, and the diff compares
     * them directly.
     */
    answerSpanMs: number | null;
  };
  /**
   * False when the trigger message could not be resolved and every offset came
   * back null. The renderer must fall back to an ordered sequence view.
   */
  hasTimeAxis: boolean;
  /**
   * False when the main flow came back with no turns at all — a different
   * failure from a missing time axis, and it needs a different explanation.
   * The backend only links a turn to its message when the answer finished
   * cleanly, so an errored or cancelled answer yields an empty main flow.
   */
  hasTurns: boolean;
  lanes: TraceLane[];
  metrics: TraceMetrics;
  /**
   * Lane kinds this source could not report, decided per trace rather than per
   * source. A replay used to be assumed blind to sub-agents; now it may or may
   * not carry them, and a diff must not call a lane "removed" when the other
   * side simply could not see it.
   */
  unavailableKinds: TraceLaneKind[];
  /** Human-readable notes about what this source cannot show. */
  degraded: string[];
};

const ERROR_STATUSES = new Set(["error", "failed", "rejected"]);
const RUNNING_STATUSES = new Set(["pending", "running", "accepted"]);
const OK_STATUSES = new Set(["success", "completed", "ok"]);

export function normalizeStatus(raw: string): TraceStatus {
  const value = raw.trim().toLowerCase();
  if (!value) return "unknown";
  if (ERROR_STATUSES.has(value)) return "error";
  if (RUNNING_STATUSES.has(value)) return "running";
  if (OK_STATUSES.has(value)) return "ok";
  if (value === "cancelled") return "cancelled";
  return "unknown";
}

/** Milliseconds between two ISO timestamps, or null if either is unparseable. */
export function msBetween(start: unknown, end: unknown): number | null {
  if (typeof start !== "string" || typeof end !== "string") return null;
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return Math.trunc(endMs - startMs);
}

export function countNodes(
  lanes: TraceLane[],
  predicate: (node: TraceNode) => boolean,
): number {
  let total = 0;
  const walk = (nodes: TraceNode[]) => {
    for (const node of nodes) {
      if (predicate(node)) total += 1;
      walk(node.children);
    }
  };
  for (const lane of lanes) walk(lane.nodes);
  return total;
}

/**
 * The parts of `[startMs, endMs]` that none of `busy` covers.
 *
 * Used to work out when a turn was genuinely waiting: the wait window minus the
 * union of its tool calls. Overlapping calls (a turn can run several at once)
 * are merged first, so a parallel batch counts once.
 */
export function subtractIntervals(
  startMs: number,
  endMs: number,
  busy: Array<{ startMs: number | null; endMs: number | null }>,
): Array<{ startMs: number; endMs: number }> {
  if (endMs <= startMs) return [];

  const clipped = busy
    .filter(
      (span): span is { startMs: number; endMs: number } =>
        span.startMs !== null && span.endMs !== null,
    )
    .map((span) => ({
      startMs: Math.max(span.startMs, startMs),
      endMs: Math.min(span.endMs, endMs),
    }))
    .filter((span) => span.endMs > span.startMs)
    .sort((a, b) => a.startMs - b.startMs);

  const gaps: Array<{ startMs: number; endMs: number }> = [];
  let cursor = startMs;
  for (const span of clipped) {
    if (span.startMs > cursor) gaps.push({ startMs: cursor, endMs: span.startMs });
    cursor = Math.max(cursor, span.endMs);
  }
  if (cursor < endMs) gaps.push({ startMs: cursor, endMs });
  return gaps;
}

/**
 * Nodes clustered into runs that genuinely ran at the same time.
 *
 * A cluster is maximal: A overlapping B and B overlapping C puts all three
 * together even when A and C do not touch, because they still cannot share one
 * row. Clusters of one are returned too — the caller decides what counts as a
 * batch.
 *
 * Touching is not overlapping. Tools run one after another close out at the
 * moment the next one starts, and that is the shape of every serial trace, so
 * `end === nextStart` must not read as concurrency.
 *
 * A node missing either endpoint is left out entirely rather than guessed at.
 * That is what keeps a trace with no time axis — where every offset is null —
 * from being reorganised on the strength of offsets it does not have.
 */
export function overlappingGroups(nodes: TraceNode[]): TraceNode[][] {
  const measured = nodes
    .filter(
      (node): node is TraceNode & { startMs: number; endMs: number } =>
        node.startMs !== null && node.endMs !== null,
    )
    .sort((a, b) => a.startMs - b.startMs);

  const groups: TraceNode[][] = [];
  let current: TraceNode[] = [];
  let reach = Number.NEGATIVE_INFINITY;

  for (const node of measured) {
    if (current.length > 0 && node.startMs < reach) {
      current.push(node);
      reach = Math.max(reach, node.endMs);
      continue;
    }
    if (current.length > 0) groups.push(current);
    current = [node];
    reach = node.endMs;
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

/**
 * Nodes laid onto as few rows as they need, so none is painted over another.
 *
 * Greedy first-fit by start: a node goes on the first row that is already free
 * at that moment, and only opens a new one when every row is still busy. Nodes
 * that never overlap therefore come back as a single row, which is what keeps a
 * serial trace drawn exactly as it was before rows existed.
 *
 * An open-ended node is packed as though it ended where it started. It is drawn
 * running to the edge of the axis, so treating that as its real extent would
 * push every later node onto a row of its own — one hung tool call turning a
 * lane into a staircase. Overdrawing it stays the lesser fault.
 */
export function packIntoTracks(nodes: TraceNode[]): TraceNode[][] {
  const tracks: TraceNode[][] = [];
  const reach: number[] = [];

  const ordered = [...nodes].sort(
    (a, b) =>
      (a.startMs ?? Number.MAX_SAFE_INTEGER) - (b.startMs ?? Number.MAX_SAFE_INTEGER),
  );

  for (const node of ordered) {
    // Not drawn at all — TraceBar skips a node with no start — so it cannot
    // collide with anything and must not open a row.
    if (node.startMs === null) {
      if (tracks.length === 0) {
        tracks.push([]);
        reach.push(Number.NEGATIVE_INFINITY);
      }
      tracks[0].push(node);
      continue;
    }

    let index = reach.findIndex((busyUntil) => busyUntil <= node.startMs!);
    if (index === -1) {
      tracks.push([]);
      reach.push(Number.NEGATIVE_INFINITY);
      index = tracks.length - 1;
    }
    tracks[index].push(node);
    reach[index] = Math.max(reach[index], node.endMs ?? node.startMs);
  }

  return tracks;
}

/**
 * A prompt block a turn was built from, as the badge that names it.
 *
 * `renderedChars` / `renderedTokens` are what that block actually cost in this
 * turn, taken from `request_params.prompt_parts`. They are carried alongside so
 * the source panel can put them next to the template's own length: the two do
 * not match — substitution and allowlist filtering happen between them — and
 * showing only one invites the reader to diff against the wrong number.
 */
export type PromptBinding = {
  name: string;
  id: string;
  version: number;
  kind?: string;
  label: string;
  renderedChars: number | null;
  renderedTokens: number | null;
};

/** The bindings a node records, paired with what each cost here. Empty if none. */
export function promptBindingsOf(detail: Record<string, unknown>): PromptBinding[] {
  const sizeOf = new Map<string, { chars: number | null; tokens: number | null }>();
  const params = mapValue(detail.request_params);
  for (const raw of arrayValue(params.prompt_parts)) {
    const part = mapValue(raw);
    const name = stringValue(part.name, "");
    if (name) {
      sizeOf.set(name, {
        chars: intValue(part.chars),
        tokens: intValue(part.est_tokens),
      });
    }
  }

  return Object.entries(mapValue(detail.prompt_bindings))
    .map(([name, value]): PromptBinding | null => {
      const binding = mapValue(value);
      const id = stringValue(binding.id, "");
      const version = intValue(binding.version);
      if (!id || version === null) return null;
      const kind = stringValue(binding.kind, "") || undefined;
      // The orchestrator profile is rendered into <sub_agent_orchestration>,
      // so its cost is recorded under that name rather than its own.
      const partName = name === "orchestrator_profile" ? "sub_agent_orchestration" : name;
      const size = sizeOf.get(partName);
      return {
        name,
        id,
        version,
        kind,
        label: `${name} v${version}`,
        renderedChars: size?.chars ?? null,
        renderedTokens: size?.tokens ?? null,
      };
    })
    .filter((binding): binding is PromptBinding => binding !== null);
}

/**
 * Lanes in reading order, each with how deep it is drawn.
 *
 * `lanes` is flat so everything that walks or pairs lanes keeps seeing all of
 * them; the tree exists only at render. Children follow their parent
 * immediately rather than being collected at the end, so a delegation and the
 * jobs inside it read as one block.
 *
 * Shared by both readings of a trace. Two views computing the hierarchy
 * separately would eventually disagree about it, and the whole point of the
 * indent is that it means the same thing in either.
 */
export function laneTree(lanes: TraceLane[]): Array<{ lane: TraceLane; depth: number }> {
  const childrenOf = new Map<string, TraceLane[]>();
  for (const lane of lanes) {
    if (!lane.parentLaneId) continue;
    const bucket = childrenOf.get(lane.parentLaneId);
    if (bucket) bucket.push(lane);
    else childrenOf.set(lane.parentLaneId, [lane]);
  }

  const out: Array<{ lane: TraceLane; depth: number }> = [];
  const walk = (lane: TraceLane, depth: number) => {
    out.push({ lane, depth });
    for (const child of childrenOf.get(lane.id) ?? []) walk(child, depth + 1);
  };
  // A child whose parent is missing — a lane kind the source could not report —
  // would otherwise never be walked, so it is treated as a root.
  const ids = new Set(lanes.map((lane) => lane.id));
  for (const lane of lanes) {
    if (lane.parentLaneId && ids.has(lane.parentLaneId)) continue;
    walk(lane, 0);
  }
  return out;
}

export type TraceScale = {
  /** Fraction of the track, 0..1, for an absolute offset. Monotonic. */
  fractionOf: (ms: number) => number;
  /** Where the rate changes, or null when the axis is plain linear. */
  breakFraction: number | null;
  breakMs: number | null;
};

/**
 * The share of the track the answer window is guaranteed when work outlives it.
 *
 * Not a tuned number so much as a floor: below about this the main lane stops
 * being readable at all, and a reviewer opens the trace to read the main lane.
 */
const MIN_HEAD_SHARE = 0.4;

/**
 * Offsets to track fractions, at one rate or two.
 *
 * A delegated answer publishes in seconds and then keeps working for up to ten
 * minutes. On one linear axis the whole synchronous answer is a couple of
 * percent of the track — every turn and tool call crushed into a sliver — while
 * the durable tail, where almost nothing happens per second, takes the rest.
 *
 * So the axis bends once, at the moment the answer was published: the window
 * before it is guaranteed `MIN_HEAD_SHARE` of the track and the tail takes what
 * is left. Tick labels stay real offsets, which is what makes this honest — they
 * simply stop being evenly spaced, and the rate change is drawn.
 *
 * It engages only when it has to. With no tail, or when the answer window
 * already claims its share, the map is the same linear one as before and every
 * existing trace is drawn to the pixel as it was.
 */
export function buildScale({
  startMs,
  endMs,
  breakMs,
}: {
  startMs: number | null;
  endMs: number | null;
  breakMs: number | null;
}): TraceScale {
  const start = startMs ?? 0;
  const end = endMs ?? start;
  const span = Math.max(end - start, 1);
  const linear = {
    fractionOf: (ms: number) => (ms - start) / span,
    breakFraction: null,
    breakMs: null,
  };

  if (breakMs === null || breakMs <= start || breakMs >= end) return linear;
  const headSpan = breakMs - start;
  const tailSpan = end - breakMs;
  if (headSpan / span >= MIN_HEAD_SHARE) return linear;

  return {
    fractionOf: (ms: number) =>
      ms <= breakMs
        ? (MIN_HEAD_SHARE * (ms - start)) / headSpan
        : MIN_HEAD_SHARE + ((1 - MIN_HEAD_SHARE) * (ms - breakMs)) / tailSpan,
    breakFraction: MIN_HEAD_SHARE,
    breakMs,
  };
}

/**
 * Tick positions for an axis, per segment when it bends.
 *
 * Evenly spaced fractions would put four of five labels inside the tail, which
 * is the part that needs them least. Each segment gets its own run instead, so
 * the answer window is labelled at its own resolution.
 */
export function axisTicks(
  scale: TraceScale,
  { startMs, endMs }: { startMs: number | null; endMs: number | null },
  intervals: number,
): Array<{ fraction: number; ms: number }> {
  const start = startMs ?? 0;
  const end = endMs ?? start;
  if (scale.breakMs === null) {
    return Array.from({ length: intervals + 1 }, (_, index) => {
      const ms = start + ((end - start) * index) / intervals;
      return { fraction: index / intervals, ms };
    });
  }

  // One fewer per segment than the plain axis gets in total: two runs at the
  // full count crowd the labels into each other, and the point of the extra
  // ones is resolution in the answer window, not more of them everywhere.
  const perSegment = Math.max(2, intervals - 1);
  const out: Array<{ fraction: number; ms: number }> = [];
  // The break itself is emitted once, as the head's last tick.
  for (let index = 0; index <= perSegment; index += 1) {
    const ms = start + ((scale.breakMs - start) * index) / perSegment;
    out.push({ fraction: scale.fractionOf(ms), ms });
  }
  for (let index = 1; index <= perSegment; index += 1) {
    const ms = scale.breakMs + ((end - scale.breakMs) * index) / perSegment;
    out.push({ fraction: scale.fractionOf(ms), ms });
  }
  return out;
}

/** Min start / max end across every node, ignoring nulls. */
export function laneBounds(nodes: TraceNode[]): {
  startMs: number | null;
  endMs: number | null;
} {
  let startMs: number | null = null;
  let endMs: number | null = null;
  const walk = (list: TraceNode[]) => {
    for (const node of list) {
      if (node.startMs !== null && (startMs === null || node.startMs < startMs)) {
        startMs = node.startMs;
      }
      if (node.endMs !== null && (endMs === null || node.endMs > endMs)) {
        endMs = node.endMs;
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return { startMs, endMs };
}
